/**
 * The panel's single error seam.
 *
 * Everything that can fail — a derivation over a half-ingested trace, a port
 * that died mid-post, a worker that never answered — reports here instead of
 * into a bare `catch {}`. One place to route from means one place to surface
 * from, and a user can see that something broke rather than wondering why the
 * timeline stopped moving.
 *
 * Reads are identity-stable: `lensErrors()` returns the same array until a
 * report lands, so React Compiler-memoized read sites recompute exactly when
 * the data moved on.
 */

export interface LensError {
  /** Which part of the panel failed — the boundary's scope, or "uncaught". */
  scope: string;
  message: string;
  stack?: string;
  /** Wall clock of the most recent occurrence. */
  at: number;
  /** Repeats of this scope+message, collapsed into one entry. */
  count: number;
  /**
   * `notice` is for things the user must know but that are not faults —
   * "recovered 900 frames", "the oldest minute left the log". Filing those as
   * errors would make the chip cry wolf, and hiding them would put the panel
   * back to losing data quietly.
   */
  level: "error" | "notice";
}

/** Reports retained. A repeating failure collapses, so this is generous. */
export const ERROR_RING_MAX = 50;

type Sink = (errors: readonly LensError[]) => void;

let errors: LensError[] = [];
/** Materialized `errors`, invalidated on every mutation. */
let snapshot: readonly LensError[] | null = null;
const byKey = new Map<string, LensError>();
const sinks = new Set<Sink>();

/** The list as it stands, stable until the next report. */
export function lensErrors(): readonly LensError[] {
  if (!snapshot) snapshot = [...errors];
  return snapshot;
}

export function subscribeErrors(sink: Sink): () => void {
  sinks.add(sink);
  return () => {
    sinks.delete(sink);
  };
}

export function clearErrors(): void {
  errors = [];
  byKey.clear();
  snapshot = null;
  notify();
}

/**
 * Record a failure. Never throws: this is the path errors travel on, so a
 * broken sink must not become a second error.
 */
export function reportError(scope: string, err: unknown): void {
  record(scope, err, "error");
}

/**
 * Something the user needs to know that is not a fault — data the panel had to
 * drop, a session it recovered. Same ring, so there is still one place to look.
 */
export function reportNotice(scope: string, message: string): void {
  record(scope, message, "notice");
}

function record(scope: string, err: unknown, level: LensError["level"]): void {
  const message = messageOf(err);
  const key = `${scope} ${message}`;
  const existing = byKey.get(key);
  if (existing) {
    existing.count++;
    existing.at = Date.now();
  } else {
    const entry: LensError = { scope, message, at: Date.now(), count: 1, level };
    const stack = err instanceof Error ? err.stack : undefined;
    if (stack !== undefined) entry.stack = stack;
    errors.push(entry);
    byKey.set(key, entry);
    if (errors.length > ERROR_RING_MAX) {
      const evicted = errors.shift();
      if (evicted) byKey.delete(`${evicted.scope} ${evicted.message}`);
    }
  }
  snapshot = null;
  notify();
}

/**
 * Anything that dispatches `error` / `unhandledrejection` — `window` in the
 * panel, a double in tests. Method syntax so both shapes fit.
 */
export interface ErrorEventTarget {
  addEventListener(type: string, listener: (event: ErrorLikeEvent) => void): void;
  removeEventListener(type: string, listener: (event: ErrorLikeEvent) => void): void;
}

/** The fields we read off `ErrorEvent` / `PromiseRejectionEvent`. */
export interface ErrorLikeEvent {
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Route what escapes React entirely — a throw in an event handler, a rejected
 * promise nobody awaited — into the same ring. Returns a disposer.
 */
export function installGlobalErrorHandlers(target: ErrorEventTarget): () => void {
  const onError = (event: ErrorLikeEvent) => {
    reportError("uncaught", event.error ?? event.message);
  };
  const onRejection = (event: ErrorLikeEvent) => {
    reportError("unhandled-rejection", event.reason);
  };
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);
  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };
}

function notify(): void {
  const current = lensErrors();
  for (const sink of sinks) {
    try {
      sink(current);
    } catch {
      // A sink that throws is not worth reporting through the sinks.
    }
  }
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (typeof (err as { message?: unknown })?.message === "string") {
    return (err as { message: string }).message;
  }
  return String(err);
}
